import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Heart, Users, Target, Lightbulb, Shield, TrendingUp, Leaf } from "lucide-react";
import { fadeUp, stagger, AnimatedSection } from "@/lib/animations";

export default function MissionPage() {
  const values = [
    { icon: Heart, title: "Missione Prima di Tutto", desc: "Tutto ciò che facciamo è al servizio degli obiettivi della tua nonprofit. Il tuo impatto guida la nostra strategia." },
    { icon: Shield, title: "Trasparenza Radicale", desc: "Nessun costo nascosto, nessuna metrica di vanità. Monitoriamo ciò che conta e condividiamo tutto con te." },
    { icon: TrendingUp, title: "Risultati che Contano", desc: "Misuriamo il successo in donatori raggiunti, volontari iscritti e vite cambiate — non solo clic." },
    { icon: Users, title: "Mentalità di Partnership", desc: "Non siamo fornitori. Siamo un'estensione del tuo team, investiti nel tuo successo a lungo termine." },
    { icon: Lightbulb, title: "Innovazione per il Bene", desc: "Portiamo strategie digitali all'avanguardia nel mondo nonprofit, rendendo accessibili strumenti di livello enterprise." },
    { icon: Target, title: "Focus Implacabile", desc: "Siamo specializzati nelle nonprofit. Ogni strategia, template e campagna è costruita specificatamente per il tuo settore." },
  ];

  return (
    <div data-testid="mission-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="mission-hero">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="max-w-3xl">
            <motion.div variants={fadeUp}>
              <Badge className="bg-[#D85A30]/10 text-[#D85A30] border-[#D85A30]/20 hover:bg-[#D85A30]/15 mb-6 px-4 py-1.5 rounded-full">
                <span className="kicker">La Nostra Missione</span>
              </Badge>
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C2C2A] leading-[1.1] mb-6">
              Esistiamo per amplificare{" "}
              <span className="text-[#D85A30]">il lavoro più importante del mondo.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base md:text-lg text-[#888780] leading-relaxed max-w-2xl">
              Givenda è nata da una convinzione semplice: le nonprofit meritano gli stessi strumenti e strategie digitali 
              dei brand più grandi — senza il prezzo enterprise. Noi colmiamo questo divario affinché la tua missione raggiunga più persone.
            </motion.p>
          </AnimatedSection>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32 bg-[#F1EFE8]/40" data-testid="mission-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">La Nostra Storia</motion.p>
                <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-semibold text-[#2C2C2A] leading-tight mb-6">
                  Nata dalla frustrazione per lo status quo
                </motion.h2>
                <motion.div variants={fadeUp} className="space-y-4 text-[#888780] leading-relaxed">
                  <p>
                    Abbiamo visto troppe nonprofit incredibili — organizzazioni che nutrono gli affamati, ospitano i senzatetto, 
                    proteggono il nostro pianeta — che faticavano a farsi trovare online. Nel frattempo, aziende a scopo di lucro 
                    con budget enormi dominavano i risultati di ricerca.
                  </p>
                  <p>
                    Sapevamo che esistono fondi che offrono 10.000€/mese in pubblicità gratuita alle nonprofit qualificate, 
                    eppure la stragrande maggioranza non ne aveva mai sentito parlare. Sapevamo che un sito web ben progettato 
                    poteva raddoppiare le donazioni. Sapevamo che la giusta strategia SEO poteva portare un ente benefico in prima pagina.
                  </p>
                  <p className="text-[#2C2C2A] font-medium">
                    Così abbiamo fondato Givenda per essere il ponte — collegando le nonprofit con le strategie digitali 
                    e gli strumenti che meritano, a prezzi accessibili.
                  </p>
                </motion.div>
              </div>
              <motion.div variants={fadeUp}>
                <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)]">
                  <img
                    src="https://images.pexels.com/photos/6248966/pexels-photo-6248966.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Team collaboration"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#085041]/30 to-transparent" />
                </div>
              </motion.div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32" data-testid="mission-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <motion.p variants={fadeUp} className="kicker text-[#D85A30] mb-3">I Nostri Valori</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2C2C2A] leading-tight">
              Ciò che guida tutto il nostro lavoro
            </motion.h2>
          </AnimatedSection>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <motion.div key={i} variants={fadeUp}>
                <Card className="bg-white border-[#D9D8D2]/50 shadow-[0_10px_30px_-10px_rgba(8,80,65,0.05)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-300 h-full group" data-testid={`value-card-${i}`}>
                  <CardContent className="p-8">
                    <div className="w-12 h-12 rounded-2xl bg-[#085041]/5 flex items-center justify-center mb-6 group-hover:bg-[#085041]/10 transition-colors">
                      <v.icon className="w-6 h-6 text-[#2C2C2A]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#2C2C2A] mb-2">{v.title}</h3>
                    <p className="text-sm text-[#888780] leading-relaxed">{v.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#085041]" data-testid="mission-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection className="max-w-2xl mx-auto">
            <motion.div variants={fadeUp} className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center">
                <Leaf className="w-8 h-8 text-[#D85A30]" />
              </div>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-semibold text-white leading-tight mb-6">
              Pronto a lavorare con un team che tiene alla tua missione quanto te?
            </motion.h2>
            <motion.div variants={fadeUp}>
              <Link to="/book-a-call">
                <Button className="rounded-full px-8 h-12 text-base bg-[#D85A30] hover:bg-[#C04E25] text-white font-medium shadow-lg transition-all hover:-translate-y-1" data-testid="mission-cta-button">
                  Parliamone
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
